<template>
  <div>
    <!-- Skeleton loader when fetching data -->
    <b-card no-body class="staff-card mb-2" v-if="loading">
       <b-card-body class="p-4 d-flex align-items-center justify-content-center" style="min-height: 400px;">
          <div class="text-center w-100">
             <div class="skeleton-loader mx-auto mb-2" style="width: 140px; height: 140px; border-radius: 50%;"></div>
             <div class="skeleton-loader mx-auto mb-1" style="width: 200px; height: 30px;"></div>
             <div class="skeleton-loader mx-auto mb-1" style="width: 150px; height: 20px;"></div>
          </div>
       </b-card-body>
    </b-card>

    <b-card no-body class="staff-card mb-2" v-else-if="staffData">
       <div class="gradient-top-line"></div>
       <b-card-body class="p-0">
          <b-row class="m-0">
            <!-- Left Sidebar (Profile Widget) -->
            <b-col cols="12" lg="4" xl="3" class="profile-sidebar border-right p-0 text-center bg-white position-relative">
               <!-- Banner Background -->
               <div class="profile-banner text-left px-2 pt-2">
                 <b-button variant="light" size="sm" @click="$router.push({ name: 'manage-staffgrid' })" class="back-btn shadow-sm font-weight-bold text-primary" style="position: relative; z-index: 10;">
                   <feather-icon icon="ArrowLeftIcon" class="mr-50" /> Back to Grid
                 </b-button>
               </div>
               
               <div class="avatar-wrapper mx-auto mb-2" style="margin-top: -60px;">
                 <b-avatar 
                   :src="getPicturePreview(staffData.picture)" 
                   size="130" 
                   variant="light-primary" 
                   class="shadow-lg profile-avatar"
                   referrerpolicy="no-referrer"
                 />
                 <div 
                   class="status-indicator" 
                   :class="staffData.synced ? 'bg-success' : 'bg-danger'"
                   v-b-tooltip.hover 
                   :title="staffData.synced ? 'Synced with mobile app' : 'User is not synced with phone to login to use mob app'"
                 ></div>
               </div>
               
               <div class="px-3 pb-3">
                 <p
                   v-if="hpgsEmpCodeLabel"
                   class="mb-1 font-weight-bolder text-primary small text-uppercase"
                   style="letter-spacing: 0.06em"
                 >
                   {{ hpgsEmpCodeLabel }}
                 </p>
                 <h3 class="font-weight-bolder mb-0 text-primary-dark name-title">{{ staffData.name || 'N/A' }}</h3>
                <p class="text-muted designation-text mb-1">{{ staffData.designation || 'Staff' }}</p>
                <div v-if="staffData.meta_streamName" class="mb-2">
                  <b-badge variant="light-info" class="text-uppercase font-weight-bolder px-1">
                    {{ staffData.meta_streamName }}
                  </b-badge>
                </div>
               
               <div class="d-flex flex-column gap-2 mt-3 p-2">
                 <div class="info-pill text-left">
                    <feather-icon icon="BriefcaseIcon" size="16" class="mr-50 text-primary" />
                    <span>{{ getDepName(staffData.departmentID) || 'No Dept' }}</span>
                 </div>
                 <div class="info-pill text-left">
                    <feather-icon icon="MapPinIcon" size="16" class="mr-50 text-primary" />
                    <span>{{ staffData.metaCampusName || getCampus(staffData.campusID) || 'No Campus' }}</span>
                 </div>
                 <div class="info-pill text-left" v-if="staffData.contact_number">
                    <feather-icon icon="PhoneIcon" size="16" class="mr-50 text-primary" />
                    <span>{{ staffData.contact_number }}</span>
                 </div>
               </div>
               </div>
            </b-col>

            <!-- Right Content Area (Tabs) -->
            <b-col cols="12" lg="8" xl="9" class="p-3 staff-details-tabs-col">
               <div class="staff-details-tabs-shell">
               <b-tabs
                 v-model="activeDetailTab"
                 nav-class="custom-nav-tabs"
                 content-class="staff-details-tab-panes"
                 active-nav-item-class="custom-nav-active"
                 no-fade
               >
                 <!-- Tab 1: Staff Details -->
                 <b-tab title="Info">
                   <div class="tab-content-wrapper p-2 animation-fade">
                      <h5 class="section-title"><feather-icon icon="UserIcon" class="mr-50" /> Personal Information</h5>
                      <b-row class="mb-3 content-grid">
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Father's Name</label>
                          <div class="info-value">{{ staffData.father_name || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Surname</label>
                          <div class="info-value">{{ staffData.surname || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Date of Birth</label>
                          <div class="info-value">{{ formatDate(staffData.date_of_birth) || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Gender</label>
                          <div class="info-value">{{ staffData.gender || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">CNIC</label>
                          <div class="info-value">{{ staffData.cnic || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Marital Status / Spouse</label>
                          <div class="info-value">{{ staffData.spouse || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Personal Email</label>
                          <div class="info-value">{{ staffData.personalEmail || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Work Email</label>
                          <div class="info-value">{{ staffData.workEmail || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Contact Number</label>
                          <div class="info-value font-weight-bold">{{ staffData.contact_number || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Emergency Contact</label>
                          <div class="info-value">{{ staffData.emergency_number || '-' }} <span v-if="staffData.emergency_contact_person" class="text-muted font-small-2">({{staffData.emergency_contact_person}})</span></div>
                        </b-col>
                        <b-col sm="6" lg="8" class="mb-2">
                          <label class="info-label">Current Address</label>
                          <div class="info-value">{{ staffData.address_current || '-' }}</div>
                        </b-col>
                        <b-col sm="12" class="mb-2">
                          <label class="info-label">Permanent Address</label>
                          <div class="info-value">{{ staffData.address_permanent || '-' }}</div>
                        </b-col>
                        <template v-if="isMyskoolOrIuss">
                          <b-col sm="6" lg="4" class="mb-2">
                            <label class="info-label">Province</label>
                            <div class="info-value">{{ staffData.province || '-' }}</div>
                          </b-col>
                          <b-col sm="6" lg="4" class="mb-2">
                            <label class="info-label">City</label>
                            <div class="info-value">{{ staffData.city || '-' }}</div>
                          </b-col>
                          <b-col sm="6" lg="4" class="mb-2">
                            <label class="info-label">LinkedIn Profile</label>
                            <div class="info-value">
                              <a
                                v-if="staffData.linkedinProfile"
                                :href="staffData.linkedinProfile"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {{ staffData.linkedinProfile }}
                              </a>
                              <span v-else>-</span>
                            </div>
                          </b-col>
                          <b-col sm="6" lg="4" class="mb-2" v-if="!formRights.hidePayroll">
                            <label class="info-label">Last Salary Drawn</label>
                            <div class="info-value">{{ staffData.previousSalary || '0' }}</div>
                          </b-col>
                          <b-col sm="6" lg="4" class="mb-2" v-if="!formRights.hidePayroll">
                            <label class="info-label">Salary Slip</label>
                            <div class="info-value">
                              <a
                                v-if="staffData.salarySlip"
                                :href="staffData.salarySlip"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View salary slip
                              </a>
                              <span v-else>-</span>
                            </div>
                          </b-col>
                          <b-col sm="6" lg="4" class="mb-2" v-if="!formRights.hideDocuments">
                            <label class="info-label">CV</label>
                            <div class="info-value">
                              <a
                                v-if="staffData.cv"
                                :href="staffData.cv"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View CV
                              </a>
                              <span v-else>-</span>
                            </div>
                          </b-col>
                        </template>
                      </b-row>

                      <h5 class="section-title mt-2"><feather-icon icon="BookIcon" class="mr-50" /> Academic & Professional Details</h5>
                      <b-row class="mb-3 content-grid">
                        <template v-if="isMyskoolIuss">
                          <b-col sm="12" class="mb-2">
                            <label class="info-label">Qualifications</label>
                            <div v-if="qualifications.length" class="iuss-details-list">
                              <div
                                v-for="(qual, index) in qualifications"
                                :key="'qual-' + index"
                                class="iuss-details-record"
                              >
                                <b-row>
                                  <b-col cols="6" sm="3" class="mb-1 mb-sm-0">
                                    <span class="info-label d-block">Degree</span>
                                    <div class="info-value">{{ qual.degree || '-' }}</div>
                                  </b-col>
                                  <b-col cols="6" sm="3" class="mb-1 mb-sm-0">
                                    <span class="info-label d-block">Subject</span>
                                    <div class="info-value">{{ qual.subject || '-' }}</div>
                                  </b-col>
                                  <b-col cols="6" sm="2" class="mb-1 mb-sm-0">
                                    <span class="info-label d-block">Institute</span>
                                    <div class="info-value">{{ qual.institute || '-' }}</div>
                                  </b-col>
                                  <b-col cols="6" sm="2" class="mb-1 mb-sm-0">
                                    <span class="info-label d-block">Year</span>
                                    <div class="info-value">{{ qual.year || '-' }}</div>
                                  </b-col>
                                  <b-col cols="6" sm="2">
                                    <span class="info-label d-block">Grade</span>
                                    <div class="info-value">{{ qual.grade || '-' }}</div>
                                  </b-col>
                                </b-row>
                              </div>
                            </div>
                            <div v-else class="info-value">-</div>
                          </b-col>
                          <b-col sm="12" class="mb-2" v-if="!formRights.hideDocuments">
                            <label class="info-label">Certificates</label>
                            <div v-if="certificates.length" class="iuss-details-list">
                              <div
                                v-for="(cert, index) in certificates"
                                :key="'cert-' + index"
                                class="iuss-details-record"
                              >
                                <b-row>
                                  <b-col cols="12" sm="4" class="mb-1 mb-sm-0">
                                    <span class="info-label d-block">Certificate</span>
                                    <div class="info-value">{{ cert.name || '-' }}</div>
                                  </b-col>
                                  <b-col cols="6" sm="4" class="mb-1 mb-sm-0">
                                    <span class="info-label d-block">Institute</span>
                                    <div class="info-value">{{ cert.institute || '-' }}</div>
                                  </b-col>
                                  <b-col cols="6" sm="4">
                                    <span class="info-label d-block">Year</span>
                                    <div class="info-value">{{ cert.year || '-' }}</div>
                                  </b-col>
                                </b-row>
                              </div>
                            </div>
                            <div v-else class="info-value">-</div>
                          </b-col>
                        </template>
                        <b-col v-else sm="6" lg="4" class="mb-2">
                          <label class="info-label">Qualification</label>
                          <div class="info-value">{{ staffData.qualification || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Experience</label>
                          <div class="info-value">{{ staffData.experience || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Teaching Level</label>
                          <div class="info-value">{{ staffData.teachingLevel || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2" v-if="!formRights.hideDocuments">
                          <label class="info-label">Documents Status</label>
                          <div>
                            <b-badge :variant="staffData.documents ? 'light-success' : 'light-secondary'" class="text-uppercase py-1 px-2 mt-50">
                               {{ staffData.documents ? 'Available' : 'Pending/None' }}
                            </b-badge>
                          </div>
                        </b-col>
                      </b-row>

                      <h5 class="section-title mt-2"><feather-icon icon="BriefcaseIcon" class="mr-50" /> Employment Details</h5>
                      <b-row class="mb-3 content-grid">
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Appointed On</label>
                          <div class="info-value">{{ formatDate(staffData.appointedOn) || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Joins At</label>
                          <div class="info-value">{{ formatDate(staffData.joinsAt) || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Probation End Date</label>
                          <div class="info-value">{{ formatDate(staffData.probationEndDate) || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Availability</label>
                          <div class="info-value">{{ staffData.availibility || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">App Sync</label>
                          <div class="info-value">
                            <b-badge 
                               :variant="staffData.synced ? 'light-success' : 'light-danger'" 
                               class="text-uppercase py-1 px-2 mt-25"
                               v-b-tooltip.hover title="This user is synced with phone to login to use mob app">
                               <feather-icon icon="SmartphoneIcon" size="14" class="mr-25"/>
                               {{ staffData.synced ? 'Synced' : 'Not Synced' }}
                            </b-badge>
                          </div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">RFID</label>
                          <div class="info-value">{{ staffData.rfID || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Employee Code</label>
                          <div class="info-value">{{ staffData.empCodeStr || staffData.empCode || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Card Number</label>
                          <div class="info-value">{{ staffData.card_number || '-' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Status</label>
                          <div>
                            <b-badge :variant="(staffData.status || '').toLowerCase() === 'present' ? 'light-success' : 'light-danger'" class="text-uppercase py-1 px-2 mt-50">
                               {{ staffData.status || 'N/A' }}
                            </b-badge>
                          </div>
                        </b-col>
                      </b-row>
                      
                      <template v-if="!formRights.hidePayroll">
                      <h5 class="section-title mt-2"><feather-icon icon="CreditCardIcon" class="mr-50" /> Payroll & Financials</h5>
                      <b-row class="mb-3 content-grid">
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Expected Salary</label>
                          <div class="info-value text-muted">{{ staffData.expectedSalary || '0' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Basic Salary</label>
                          <div class="info-value font-weight-bold text-success">{{ staffData.basicSalary || '0' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Bonuses</label>
                          <div class="info-value text-info">{{ staffData.bonuses || '0' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Total Salary</label>
                          <div class="info-value font-weight-bold text-primary">{{ staffData.totalSalary || '0' }}</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Deductions Enrolled</label>
                          <div class="info-value">
                             <b-badge :variant="staffData.eobiDeduct ? 'light-primary' : 'light-secondary'" class="mr-50">EOBI</b-badge>
                             <b-badge :variant="staffData.pfDeduct ? 'light-primary' : 'light-secondary'">PF</b-badge>
                          </div>
                        </b-col>
                        <b-col sm="6" lg="6" class="mb-2">
                          <label class="info-label">Account details</label>
                          <div class="info-value">{{ staffData.payMode || 'Cash' }} <br/> 
                            <small class="text-muted" v-if="staffData.accountTitle || staffData.accountNumber">
                               {{ staffData.accountTitle ? staffData.accountTitle + ' - ' : '' }} {{ staffData.accountNumber || 'N/A' }} 
                            </small>
                          </div>
                        </b-col>
                      </b-row>
                      </template>

                      <template v-if="!formRights.hidePayroll">
                      <h5 class="section-title mt-2"><feather-icon icon="CalendarIcon" class="mr-50" /> Leave & Attendance Configuration</h5>
                      <b-row class="mb-3 content-grid">
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Leaves Allowed From</label>
                          <div class="info-value">{{ formatDate(staffData.leaves_allowed_from) || '-' }}</div>
                        </b-col>
                        <!-- dynamic_leave_balance OFF: classic remaining fields -->
                        <template v-if="!isDynamicLeaveBalance">
                          <b-col sm="6" lg="4" class="mb-2">
                            <label class="info-label">Remaining Leaves</label>
                            <div class="info-value text-primary font-weight-bold">{{ staffData.remaining_leaves || '0' }} <span v-if="staffData.allowedLeaves" class="text-muted font-small-2 font-weight-normal">/ {{ staffData.allowedLeaves }} total</span></div>
                          </b-col>
                          <b-col sm="6" lg="4" class="mb-2">
                            <label class="info-label">Remaining Sick Leaves</label>
                            <div class="info-value text-warning font-weight-bold">{{ staffData.remaining_sickLeaves || '0' }}</div>
                          </b-col>
                        </template>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Grace Mins</label>
                          <div class="info-value">{{ staffData.graceMins || '0' }} mins</div>
                        </b-col>
                        <b-col sm="6" lg="4" class="mb-2">
                          <label class="info-label">Part Timer Days</label>
                          <div class="info-value">{{ staffData.partTimerDays || '-' }}</div>
                        </b-col>
                      </b-row>
                      <!-- dynamic_leave_balance ON: leave-type wise remaining -->
                      <div v-if="isDynamicLeaveBalance" class="mb-3">
                        <label class="info-label d-block mb-1">Leave Balances (by type)</label>
                        <div v-if="leaveTypesLoading" class="text-muted font-small-2">
                          Loading leave balances…
                        </div>
                        <div v-else-if="!leaveTypeBalances.length" class="text-muted font-small-2">
                          No leave types found for this staff member
                        </div>
                        <b-row v-else class="content-grid">
                          <b-col
                            v-for="lt in leaveTypeBalances"
                            :key="lt.value"
                            sm="6"
                            lg="4"
                            class="mb-2"
                          >
                            <label class="info-label">{{ lt.text }}</label>
                            <div
                              class="info-value font-weight-bold"
                              :class="Number(lt.remaining) > 0 ? 'text-primary' : 'text-danger'"
                            >
                              {{ formatLeaveCount(lt.remaining) }}
                              <span
                                v-if="lt.allowed != null"
                                class="text-muted font-small-2 font-weight-normal"
                              >
                                / {{ formatLeaveCount(lt.allowed) }} total
                              </span>
                            </div>
                          </b-col>
                        </b-row>
                      </div>
                      </template>
                      
                      <div v-if="(staffData.status || '').toLowerCase() === 'left' || staffData.resginationDate || staffData.leaving_date">
                          <h5 class="section-title mt-2 text-danger"><feather-icon icon="LogOutIcon" class="mr-50" /> Leaving & Resignation</h5>
                          <b-row class="content-grid" style="border-color: #ffcccc; background-color: #fffbfa;">
                            <b-col sm="6" lg="4" class="mb-2">
                              <label class="info-label text-danger">Resignation Date</label>
                              <div class="info-value">{{ formatDate(staffData.resginationDate) || '-' }}</div>
                            </b-col>
                            <b-col sm="6" lg="4" class="mb-2">
                              <label class="info-label text-danger">Leaving Date</label>
                              <div class="info-value font-weight-bold">{{ formatDate(staffData.leaving_date) || '-' }}</div>
                            </b-col>
                            <b-col sm="6" lg="4" class="mb-2">
                              <label class="info-label text-danger">Leaving Category</label>
                              <div class="info-value">{{ staffData.leavingCategory || '-' }}</div>
                            </b-col>
                            <b-col sm="12" class="mb-2">
                              <label class="info-label text-danger">Leaving Reason</label>
                              <div
                                v-if="isOtherSpecifyLeavingReason"
                                class="info-value"
                              >
                                <div class="font-weight-bold">
                                  Other (Specify)
                                </div>
                                <div class="mt-25">
                                  {{ otherSpecifyLeavingReasonText || "-" }}
                                </div>
                              </div>
                              <div v-else class="info-value">
                                {{ staffData.leavingReason || "-" }}
                              </div>
                            </b-col>
                          </b-row>
                      </div>
                   </div>
                 </b-tab>

                  <!-- Tab 2: Transfer History -->
                  <b-tab title="Transfer History">
                     <div class="tab-content-wrapper scrollable-tab-content p-2 animation-fade">
                       <ul class="vuexy-timeline list-unstyled mt-2 px-1" v-if="transferHistory && transferHistory.length">
                        <li class="timeline-item" v-for="(th, index) in transferHistory" :key="index">
                          <span class="timeline-point">
                             <feather-icon icon="RefreshCwIcon" size="16" />
                          </span>
                          <div class="timeline-event animation-fade">
                            <div class="d-flex justify-content-between flex-sm-row flex-column mb-sm-0 mb-1">
                              <h5 class="text-primary font-weight-bolder mb-50">
                                <feather-icon icon="HomeIcon" size="16" class="mr-25"/>
                                {{ th.fromCampus }} 
                                <feather-icon icon="ArrowRightIcon" size="14" class="mx-50 text-muted"/> 
                                {{ th.toCampus }}
                              </h5>
                              <b-badge variant="light-secondary" class="timeline-badge align-self-start">
                                <feather-icon icon="CalendarIcon" size="12" class="mr-25"/>
                                {{ th.transferDate ? th.transferDate.split('T')[0] : 'N/A' }}
                              </b-badge>
                            </div>
                            <b-row class="mt-2">
                               <b-col :md="formRights.hidePayroll ? 12 : 7" class="mb-1 mb-md-0">
                                  <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing"><feather-icon icon="FileTextIcon" size="12" class="mr-25"/> Transfer Notes</h6>
                                  <p class="mb-0 font-weight-normal text-body">{{ th.transferNotes || 'No notes provided.' }}</p>
                               </b-col>
                               <b-col md="5" class="text-md-right text-left" v-if="!formRights.hidePayroll">
                                  <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing"><feather-icon icon="CreditCardIcon" size="12" class="mr-25"/> Total Salary</h6>
                                  <div class="font-medium-1">
                                    <span class="text-danger" style="text-decoration: line-through;">{{ th.previousTotalSalary || 0 }}</span>
                                    <feather-icon icon="ArrowRightIcon" size="14" class="mx-50 text-muted" />
                                    <span class="text-success font-weight-bolder">{{ th.newTotalSalary || 0 }}</span>
                                  </div>
                               </b-col>
                            </b-row>
                          </div>
                        </li>
                      </ul>
                      <div v-else class="text-center p-5 empty-state-box">
                        <feather-icon icon="RepeatIcon" size="50" class="text-muted mb-2 empty-state-icon" />
                        <h4 class="empty-state-title">No Transfer History Available</h4>
                        <p class="empty-state-message">This staff member hasn't been transferred to any other campus yet.</p>
                      </div>
                    </div>
                 </b-tab>

                 <!-- Tab 3: Attendance -->
                 <b-tab title="Attendance">
                    <div class="tab-content-wrapper scrollable-tab-content p-2 animation-fade">
                      <ul class="vuexy-timeline list-unstyled mt-2 px-1" v-if="attendance && attendance.length">
                        <li class="timeline-item" v-for="(att, idx) in attendance" :key="idx">
                          <span class="timeline-point">
                             <feather-icon :icon="att.status === 'P' ? 'CheckIcon' : (att.status === 'Off' ? 'CoffeeIcon' : 'XIcon')" size="16" />
                          </span>
                          <div class="timeline-event animation-fade">
                            <div class="d-flex justify-content-between flex-sm-row flex-column mb-sm-0 mb-1">
                              <div>
                                <h5 class="font-weight-bolder mb-25" :class="att.status === 'P' ? 'text-success' : (att.status === 'Off' ? 'text-secondary' : 'text-danger')">
                                  <feather-icon icon="CalendarIcon" size="16" class="mr-25"/>
                                  {{ att.date ? att.date.split('T')[0] : 'N/A' }}
                                </h5>
                                <div class="text-muted font-small-2 font-weight-bold" v-if="att.metaCampusName">
                                  <feather-icon icon="MapPinIcon" size="10" class="mr-25"/> {{ att.metaCampusName }}
                                </div>
                              </div>
                              <b-badge :variant="att.status === 'P' ? 'light-success' : (att.status === 'Off' ? 'light-secondary' : 'light-danger')" class="timeline-badge align-self-start mt-50 mt-sm-0">
                                {{ att.status === 'P' ? 'Present' : (att.status === 'Off' ? 'Off Day' : 'Absent') }}
                              </b-badge>
                            </div>
                            <b-row class="mt-2" v-if="att.status === 'P'">
                               <b-col md="6" class="mb-1 mb-md-0">
                                  <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing"><feather-icon icon="ClockIcon" size="12" class="mr-25"/> Time In</h6>
                                  <div class="font-weight-bold">{{ att.timeIn || '--:--' }}</div>
                               </b-col>
                               <b-col md="6">
                                  <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing"><feather-icon icon="ClockIcon" size="12" class="mr-25"/> Time Out</h6>
                                  <div class="font-weight-bold">{{ att.timeOut || '--:--' }}</div>
                               </b-col>
                            </b-row>
                          </div>
                        </li>
                      </ul>
                      <div v-else class="text-center p-5 empty-state-box">
                        <feather-icon icon="CalendarIcon" size="50" class="text-muted mb-2 empty-state-icon" />
                        <h4 class="empty-state-title">No Attendance Records</h4>
                      </div>
                    </div>
                 </b-tab>

                 <!-- Tab 4: Increment History -->
                 <b-tab title="Increment" v-if="!formRights.hidePayroll">
                    <div class="tab-content-wrapper scrollable-tab-content p-2 animation-fade">
                      <ul class="vuexy-timeline list-unstyled mt-2 px-1" v-if="incrementHistory && incrementHistory.length">
                        <li class="timeline-item" v-for="(inc, idx) in incrementHistory" :key="idx">
                          <span class="timeline-point">
                             <feather-icon icon="TrendingUpIcon" size="16" />
                          </span>
                          <div class="timeline-event animation-fade">
                            <div class="d-flex justify-content-between flex-sm-row flex-column mb-sm-0 mb-1">
                              <div>
                                <h5 class="text-success font-weight-bolder mb-25">
                                  <span class="mr-25 font-weight-bold">Rs.</span>
                                  +{{ inc.amount }} Increment
                                </h5>
                                <div class="text-muted font-small-2 font-weight-bold" v-if="inc.metaCampusName">
                                  <feather-icon icon="MapPinIcon" size="10" class="mr-25"/> {{ inc.metaCampusName }}
                                </div>
                              </div>
                              <b-badge variant="light-primary" class="timeline-badge align-self-start mt-50 mt-sm-0">
                                <feather-icon icon="CalendarIcon" size="12" class="mr-25"/>
                                {{ inc.date ? inc.date.split('T')[0] : 'N/A' }}
                              </b-badge>
                            </div>
                            <p class="mb-0 mt-2 font-weight-normal text-muted" v-if="inc.note">
                              <feather-icon icon="FileTextIcon" size="12" class="mr-25"/> {{ inc.note }}
                            </p>
                          </div>
                        </li>
                      </ul>
                      <div v-else class="text-center p-5 empty-state-box">
                        <feather-icon icon="TrendingUpIcon" size="50" class="text-muted mb-2 empty-state-icon" />
                        <h4 class="empty-state-title">No Increment History</h4>
                      </div>
                    </div>
                 </b-tab>

                 <!-- Tab 5: Security Deposit -->
                 <b-tab title="Security Deposit" v-if="!formRights.hidePayroll">
                    <div class="tab-content-wrapper scrollable-tab-content p-2 animation-fade">
                      <ul class="vuexy-timeline list-unstyled mt-2 px-1" v-if="securityDeposits && securityDeposits.length">
                        <li class="timeline-item" v-for="(deposit, idx) in securityDeposits" :key="idx">
                          <span class="timeline-point">
                             <feather-icon icon="ShieldIcon" size="16" />
                          </span>
                          <div class="timeline-event animation-fade">
                            <div class="d-flex justify-content-between flex-sm-row flex-column mb-sm-0 mb-1">
                              <div>
                                <h5 class="text-primary font-weight-bolder mb-25 text-capitalize">
                                  <feather-icon icon="BriefcaseIcon" size="16" class="mr-25"/>
                                  {{ deposit.loanType }}
                                </h5>
                                <div class="text-muted font-small-2 font-weight-bold" v-if="deposit.metaCampusName">
                                  <feather-icon icon="MapPinIcon" size="10" class="mr-25"/> {{ deposit.metaCampusName }}
                                </div>
                              </div>
                              <b-badge variant="light-secondary" class="timeline-badge align-self-start mt-50 mt-sm-0">
                                <feather-icon icon="CalendarIcon" size="12" class="mr-25"/>
                                {{ deposit.date ? deposit.date.split('T')[0] : 'N/A' }}
                              </b-badge>
                            </div>
                            <b-row class="mt-2">
                               <b-col md="4" class="mb-1 mb-md-0">
                                  <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing">Amount</h6>
                                  <div class="font-weight-bold text-danger">{{ deposit.amount }}</div>
                               </b-col>
                               <b-col md="4" class="mb-1 mb-md-0 border-left">
                                  <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing">Months</h6>
                                  <div class="font-weight-bold">{{ deposit.months }}</div>
                               </b-col>
                               <b-col md="4" class="border-left">
                                  <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing">Monthly Installment</h6>
                                  <div class="font-weight-bold text-warning">{{ deposit.monthlyInstallments }} / mo</div>
                               </b-col>
                            </b-row>
                          </div>
                        </li>
                      </ul>
                      <div v-else class="text-center p-5 empty-state-box">
                        <feather-icon icon="ShieldOffIcon" size="50" class="text-muted mb-2 empty-state-icon" />
                        <h4 class="empty-state-title">No Security Deposit</h4>
                      </div>
                    </div>
                 </b-tab>

                 <!-- Tab 6: Loan & Advances -->
                 <b-tab title="Loan & Advances" v-if="!formRights.hidePayroll">
                    <div class="tab-content-wrapper scrollable-tab-content p-2 animation-fade">
                      <ul class="vuexy-timeline list-unstyled mt-2 px-1" v-if="filteredLoans && filteredLoans.length">
                        <li class="timeline-item" v-for="(loan, idx) in filteredLoans" :key="idx">
                          <span class="timeline-point">
                             <feather-icon icon="CreditCardIcon" size="16" />
                          </span>
                          <div class="timeline-event animation-fade">
                            <div class="d-flex justify-content-between flex-sm-row flex-column mb-sm-0 mb-1">
                              <div>
                                <h5 class="text-primary font-weight-bolder mb-25 text-capitalize">
                                  <feather-icon icon="BriefcaseIcon" size="16" class="mr-25"/>
                                  {{ loan.loanType }}
                                </h5>
                                <div class="text-muted font-small-2 font-weight-bold" v-if="loan.metaCampusName">
                                  <feather-icon icon="MapPinIcon" size="10" class="mr-25"/> {{ loan.metaCampusName }}
                                </div>
                              </div>
                              <b-badge variant="light-secondary" class="timeline-badge align-self-start mt-50 mt-sm-0">
                                <feather-icon icon="CalendarIcon" size="12" class="mr-25"/>
                                {{ loan.date ? loan.date.split('T')[0] : 'N/A' }}
                              </b-badge>
                            </div>
                            <b-row class="mt-2">
                               <b-col md="4" class="mb-1 mb-md-0">
                                  <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing">Amount</h6>
                                  <div class="font-weight-bold text-danger">{{ loan.amount }}</div>
                               </b-col>
                               <b-col md="4" class="mb-1 mb-md-0 border-left">
                                  <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing">Months</h6>
                                  <div class="font-weight-bold">{{ loan.months }}</div>
                               </b-col>
                               <b-col md="4" class="border-left">
                                  <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing">EMI</h6>
                                  <div class="font-weight-bold text-warning">{{ loan.monthlyInstallments }} / mo</div>
                               </b-col>
                            </b-row>
                          </div>
                        </li>
                      </ul>
                      <div v-else class="text-center p-5 empty-state-box">
                        <feather-icon icon="CreditCardIcon" size="50" class="text-muted mb-2 empty-state-icon" />
                        <h4 class="empty-state-title">No Loans & Advances</h4>
                      </div>
                    </div>
                 </b-tab>

                 <!-- Tab: Salary History -->
                 <b-tab title="Salary" v-if="!formRights.hidePayroll">
                    <div class="tab-content-wrapper scrollable-tab-content p-2 animation-fade">
                      <ul class="vuexy-timeline list-unstyled mt-2 px-1" v-if="sortedSalaryRecords && sortedSalaryRecords.length">
                        <li class="timeline-item" v-for="(row, idx) in sortedSalaryRecords" :key="row.id || idx">
                          <span class="timeline-point timeline-point--rs">
                             <span class="rs-mark">Rs.</span>
                          </span>
                          <div class="timeline-event animation-fade">
                            <div class="d-flex justify-content-between flex-sm-row flex-column mb-sm-0 mb-1">
                              <div>
                                <h5 class="text-primary font-weight-bolder mb-25">
                                  <feather-icon icon="CalendarIcon" size="16" class="mr-25"/>
                                  {{ salaryPeriodLabel(row) }}
                                </h5>
                                <div class="text-muted font-small-2 font-weight-bold" v-if="row.metaCampusName">
                                  <feather-icon icon="MapPinIcon" size="10" class="mr-25"/> {{ row.metaCampusName }}
                                </div>
                              </div>
                              <b-badge variant="light-success" class="timeline-badge align-self-start mt-50 mt-sm-0">
                                Net: {{ formatMoney(row.netSalary) }}
                              </b-badge>
                            </div>
                            <b-row class="mt-2">
                              <b-col sm="6" md="3" class="mb-2 mb-md-0">
                                <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing">Gross</h6>
                                <div class="font-weight-bold text-success">{{ formatMoney(row.grossSalary) }}</div>
                              </b-col>
                              <b-col sm="6" md="3" class="mb-2 mb-md-0 border-left">
                                <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing">Deductions</h6>
                                <div class="font-weight-bold text-danger">{{ formatMoney(row.totalDedcutions) }}</div>
                              </b-col>
                              <b-col sm="6" md="3" class="mb-2 mb-md-0 border-left">
                                <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing">Basic</h6>
                                <div class="font-weight-bold">{{ formatMoney(row.basicSalary) }}</div>
                              </b-col>
                              <b-col sm="6" md="3" class="border-left">
                                <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing">Allowances</h6>
                                <div class="font-weight-bold">{{ formatMoney(row.totalAllownaces) }}</div>
                              </b-col>
                            </b-row>
                            <b-row class="mt-2 pt-2 border-top">
                              <b-col cols="6" sm="4" md="2" class="mb-2">
                                <span class="text-muted font-small-3 d-block">Present</span>
                                <span class="font-weight-bold">{{ row.present != null && row.present !== '' ? row.present : '—' }}</span>
                              </b-col>
                              <b-col cols="6" sm="4" md="2" class="mb-2">
                                <span class="text-muted font-small-3 d-block">Absent</span>
                                <span class="font-weight-bold">{{ row.absent != null && row.absent !== '' ? row.absent : '—' }}</span>
                              </b-col>
                              <b-col cols="6" sm="4" md="2" class="mb-2">
                                <span class="text-muted font-small-3 d-block">Leave</span>
                                <span class="font-weight-bold">{{ row.leave != null && row.leave !== '' ? row.leave : '—' }}</span>
                              </b-col>
                              <b-col cols="6" sm="4" md="2" class="mb-2">
                                <span class="text-muted font-small-3 d-block">Working days</span>
                                <span class="font-weight-bold">{{ row.workingDays != null && row.workingDays !== '' ? row.workingDays : '—' }}</span>
                              </b-col>
                              <b-col cols="6" sm="4" md="2" class="mb-2">
                                <span class="text-muted font-small-3 d-block">Advance / Loan</span>
                                <span class="font-small-2">{{ formatMoney(row.advanceDeduction) }} / {{ formatMoney(row.loanDeduction) }}</span>
                              </b-col>
                              <b-col cols="6" sm="4" md="2" class="mb-2">
                                <span class="text-muted font-small-3 d-block">Salary date</span>
                                <span class="font-small-2 font-weight-bold">{{ row.date ? row.date.split('T')[0] : '—' }}</span>
                              </b-col>
                            </b-row>
                            <p class="mb-0 mt-2 font-weight-normal text-muted font-small-3" v-if="row.note">
                              <feather-icon icon="FileTextIcon" size="12" class="mr-25"/> {{ row.note }}
                            </p>
                          </div>
                        </li>
                      </ul>
                      <div v-else class="text-center p-5 empty-state-box">
                        <div class="empty-state-rs mb-2" aria-hidden="true">Rs.</div>
                        <h4 class="empty-state-title">No Salary Records</h4>
                        <p class="empty-state-message text-muted mb-0">Payroll history will appear here when available.</p>
                      </div>
                    </div>
                 </b-tab>

                 <!-- Tab 7: Leaves -->
                 <b-tab title="Leaves" v-if="!formRights.hidePayroll">
                    <div class="tab-content-wrapper scrollable-tab-content p-2 animation-fade">
                      <ul class="vuexy-timeline list-unstyled mt-2 px-1" v-if="leaveRequests && leaveRequests.length">
                        <li class="timeline-item" v-for="(leave, idx) in leaveRequests" :key="idx">
                          <span class="timeline-point">
                             <feather-icon :icon="leave.status === 'Approved' ? 'CheckCircleIcon' : (leave.status === 'Rejected' ? 'XCircleIcon' : 'ClockIcon')" size="16" />
                          </span>
                          <div class="timeline-event animation-fade">
                            <div class="d-flex justify-content-between flex-sm-row flex-column mb-sm-0 mb-1">
                              <div>
                                <h5 class="text-primary font-weight-bolder mb-25">
                                  <feather-icon icon="UmbrellaIcon" size="16" class="mr-25"/>
                                  {{ leave.leaveType }}
                                </h5>
                                <div class="text-muted font-small-2 font-weight-bold" v-if="leave.metaCampusName">
                                  <feather-icon icon="MapPinIcon" size="10" class="mr-25"/> {{ leave.metaCampusName }}
                                </div>
                              </div>
                              <b-badge :variant="leave.status === 'Approved' ? 'light-success' : (leave.status === 'Rejected' ? 'light-danger' : 'light-warning')" class="timeline-badge align-self-start mt-50 mt-sm-0">
                                {{ leave.status }}
                              </b-badge>
                            </div>
                            <b-row class="mt-2">
                               <b-col md="5" class="mb-1 mb-md-0">
                                  <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing"><feather-icon icon="CalendarIcon" size="12" class="mr-25"/> Duration ({{ leave.totalDays }} Days)</h6>
                                  <div class="font-weight-bold">
                                     <span class="text-secondary">{{ leave.dtFrom ? leave.dtFrom.split('T')[0] : 'N/A' }}</span>
                                     <feather-icon icon="ArrowRightIcon" size="14" class="mx-50 text-muted" />
                                     <span class="text-secondary">{{ leave.dtTo ? leave.dtTo.split('T')[0] : 'N/A' }}</span>
                                  </div>
                               </b-col>
                               <b-col md="7" v-if="leave.note || leave.exNote" class="border-left pl-md-2">
                                  <h6 class="text-muted font-small-3 mb-25 text-uppercase letter-spacing"><feather-icon icon="FileTextIcon" size="12" class="mr-25"/> Remarks</h6>
                                  <p class="mb-0 font-weight-normal font-small-3">{{ leave.note || leave.exNote }}</p>
                               </b-col>
                            </b-row>
                          </div>
                        </li>
                      </ul>
                      <div v-else class="text-center p-5 empty-state-box">
                        <feather-icon icon="CalendarIcon" size="50" class="text-muted mb-2 empty-state-icon" />
                        <h4 class="empty-state-title">No Leave Requests</h4>
                      </div>
                    </div>
                 </b-tab>

               </b-tabs>
               </div>
            </b-col>
          </b-row>
       </b-card-body>
    </b-card>

    <!-- Error State -->
    <b-card no-body class="staff-card mb-2 text-center" v-else>
       <b-card-body class="p-5">
          <feather-icon icon="AlertCircleIcon" size="50" class="text-warning mb-2" />
          <h3 class="text-warning font-weight-bolder">Unable to load data!</h3>
          <p class="text-muted">Please try accessing this staff member via the Grid.</p>
          <b-button variant="primary" @click="$router.push({ name: 'manage-staffgrid' })">Go Back</b-button>
       </b-card-body>
    </b-card>

  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BAvatar, BBadge, BButton, BTabs, BTab
} from "bootstrap-vue";
import { mapActions } from "vuex";
import { getMediaPreviewUrl } from "@/utils/gdriveUpload";

export default {
  components: {
    BCard, BCardBody, BRow, BCol, BAvatar, BBadge, BButton, BTabs, BTab
  },
  data() {
    return {
      staffID: null,
      loading: true,
      activeDetailTab: 0,
      formRights: {},
      staffData: null,
      transferHistory: [],
      attendance: [],
      salary: [],
      leaveRequests: [],
      loansAndAdvances: [],
      incrementHistory: [],
      qualifications: [],
      certificates: [],
      campuses: [],
      departments: [],
      dynamicLeaveBalanceSetting: null,
      leaveTypeBalances: [],
      leaveTypesLoading: false,
      attendanceFields: [
        { key: "date", label: "Date", sortable: true },
        { key: "timeIn", label: "Time In", sortable: false },
        { key: "timeOut", label: "Time Out", sortable: false },
        { key: "status", label: "Status", sortable: true },
      ],
      leaveFields: [
        { key: "leaveType", label: "Type", sortable: true },
        { key: "dtFrom", label: "From", sortable: true },
        { key: "dtTo", label: "To", sortable: true },
        { key: "totalDays", label: "Days", sortable: true },
        { key: "status", label: "Status", sortable: true },
      ],
      loanFields: [
        { key: "date", label: "Date", sortable: true },
        { key: "loanType", label: "Type", sortable: true },
        { key: "amount", label: "Amount", sortable: true },
        { key: "months", label: "Months", sortable: true },
        { key: "monthlyInstallments", label: "EMI", sortable: true },
      ],
      incrementFields: [
        { key: "date", label: "Date", sortable: true },
        { key: "amount", label: "Increment", sortable: true },
        { key: "note", label: "Note", sortable: false },
      ],
    };
  },
  computed: {
    reportDomain() {
      return (this.$store.state.report || "").replace(/_/g, "");
    },
    isMyskoolIuss() {
      return this.reportDomain === "myskooliuss";
    },
    isMyskoolOrIuss() {
      return (
        this.reportDomain === "myskool" || this.reportDomain === "myskooliuss"
      );
    },
    parsedLeavingReason() {
      const raw = String(
        (this.staffData && this.staffData.leavingReason) || ""
      ).trim();
      if (!raw) {
        return { isOther: false, label: "", detail: "" };
      }
      const match = raw.match(/^Other\s*\(\s*Specify\s*\)\s*[:\-–]?\s*(.*)$/i);
      if (match) {
        return {
          isOther: true,
          label: "Other (Specify)",
          detail: (match[1] || "").trim(),
        };
      }
      return { isOther: false, label: "", detail: raw };
    },
    isOtherSpecifyLeavingReason() {
      return this.parsedLeavingReason.isOther;
    },
    otherSpecifyLeavingReasonText() {
      return this.parsedLeavingReason.detail;
    },
    /** HPGS: show EMP# above name when emp code exists */
    hpgsEmpCodeLabel() {
      if (this.reportDomain !== "myskoolhpgs" || !this.staffData) return "";
      const raw = this.staffData.empCodeStr ?? this.staffData.empCode;
      if (raw === null || raw === undefined || String(raw).trim() === "") {
        return "";
      }
      return `EMP#${String(raw).trim()}`;
    },
    filteredLoans() {
      return (this.loansAndAdvances || []).filter(
        (loan) => !(loan.loanType || '').toLowerCase().includes('security')
      );
    },
    securityDeposits() {
      return (this.loansAndAdvances || []).filter(
        (loan) => (loan.loanType || '').toLowerCase().includes('security')
      );
    },
    sortedSalaryRecords() {
      const list = [...(this.salary || [])];
      return list.sort((a, b) => {
        const yA = Number(a.year) || 0;
        const yB = Number(b.year) || 0;
        if (yB !== yA) return yB - yA;
        const mA = Number(a.month) || 0;
        const mB = Number(b.month) || 0;
        if (mB !== mA) return mB - mA;
        const dA = a.date ? new Date(a.date).getTime() : 0;
        const dB = b.date ? new Date(b.date).getTime() : 0;
        return dB - dA;
      });
    },
    isDynamicLeaveBalance() {
      const s = this.dynamicLeaveBalanceSetting;
      if (!s) return false;
      if (s.valueBool === true) return true;
      const v = s.value;
      return (
        v === true ||
        v === 1 ||
        String(v).toLowerCase() === "true" ||
        String(v) === "1"
      );
    },
  },
  async created() {
    const right = this.$store.state.rights["manage-staffgrid"] || {};
    this.formRights = right;

    this.staffID = this.$route.params.id;
    await this.loadCampusLeaveSettings();
    await this.LoadDepartments();
    await this.LoadCampuses();
    if (this.staffID) {
      await this.fetchStaffData();
      if (this.isDynamicLeaveBalance) {
        await this.loadLeaveTypeBalances();
      }
    } else {
      this.loading = false;
    }
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),

    async loadCampusLeaveSettings() {
      try {
        const res = await this.post({
          url:
            this.$store.state.domain +
            "Settings/getSettingsAll?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: ["dynamic_leave_balance"],
          context: this,
          token: this.$store.state.userData.token,
          subjects: true,
        });
        const list = Array.isArray(res) ? res : [];
        this.dynamicLeaveBalanceSetting =
          list.find((el) => el.key === "dynamic_leave_balance") || null;
      } catch (error) {
        console.error("Error loading leave settings:", error);
        this.dynamicLeaveBalanceSetting = null;
      }
    },

    extractLeaveTypesList(res) {
      if (!res) return [];
      if (Array.isArray(res)) return res;
      if (Array.isArray(res.data)) return res.data;
      if (res.data && Array.isArray(res.data.data)) return res.data.data;
      if (res.status === "success" && res.data && Array.isArray(res.data)) {
        return res.data;
      }
      return [];
    },

    leaveTypeNum(item, ...keys) {
      for (let i = 0; i < keys.length; i += 1) {
        const k = keys[i];
        if (item[k] != null && item[k] !== "") {
          const n = Number(item[k]);
          if (!Number.isNaN(n)) return n;
        }
      }
      return null;
    },

    mapLeaveTypeOption(item) {
      const name = item.name || item.leaveType || item.title || "";
      const allowed = this.leaveTypeNum(
        item,
        "allowed",
        "Allowed",
        "count",
        "Count",
        "total",
        "Total"
      );
      const utilized = this.leaveTypeNum(
        item,
        "utilized",
        "Utilized",
        "used",
        "Used"
      );
      let remaining = this.leaveTypeNum(
        item,
        "remaining",
        "Remaining",
        "balance",
        "Balance"
      );
      if (remaining == null && allowed != null) {
        remaining = allowed;
      }
      return {
        value: name,
        text: name,
        allowed,
        utilized,
        remaining,
      };
    },

    formatLeaveCount(val) {
      if (val == null || val === "" || Number.isNaN(Number(val))) return "—";
      return Number(val);
    },

    async loadLeaveTypeBalances() {
      if (!this.staffID) {
        this.leaveTypeBalances = [];
        return;
      }
      this.leaveTypesLoading = true;
      try {
        const campusID =
          (this.staffData && this.staffData.cId) ||
          this.$store.state.userData.cId;
        const url =
          this.$store.state.domain +
          "LeaveType/GetLeaveTypes?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          campusID +
          "&tID=" +
          this.staffID;

        const res = await this.get({
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });
        const list = this.extractLeaveTypesList(res);
        this.leaveTypeBalances = list.map((item) =>
          this.mapLeaveTypeOption(item)
        );
      } catch (error) {
        console.error("Error loading leave type balances:", error);
        this.leaveTypeBalances = [];
      } finally {
        this.leaveTypesLoading = false;
      }
    },

    getPicturePreview(url) {
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },

    async fetchStaffData() {
      this.loading = true;
      this.qualifications = [];
      this.certificates = [];
      try {
        const url = this.$store.state.domain +
          "Staff/ViewStaffData?db=" +
          this.$store.state.userData.db +
          "&staffID=" + this.staffID;

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
          context: this,
        };
        
        const response = await this.get(obj);
        console.log("ViewStaffData raw response:", JSON.stringify(response)?.substring(0, 500));
        
        let profile = null;
        let pHistory = [];
        let pAttendance = [];
        let pSalary = [];
        let pLeaveRequests = [];
        let pLoansAndAdvances = [];
        let pIncrementHistory = [];
        
        // Helper: recursively find an object with a given key anywhere in the response
        const deepFind = (obj, key) => {
          if (!obj || typeof obj !== 'object') return null;
          if (obj[key] !== undefined) return obj;
          for (const k of Object.keys(obj)) {
            if (obj[k] && typeof obj[k] === 'object' && !Array.isArray(obj[k])) {
              const found = deepFind(obj[k], key);
              if (found) return found;
            }
          }
          return null;
        };
        
        let resData = response;
        if (typeof resData === 'string') {
            try { resData = JSON.parse(resData); } catch(e){}
        }

        if (resData) {
            // Unpack layered data envelopes
            if (resData.staffProfile) {
                profile = resData.staffProfile;
                pHistory = resData.transferHistory || [];
                pAttendance = resData.attendance || [];
                pSalary = resData.salary || [];
                pLeaveRequests = resData.leaveRequests || [];
                pLoansAndAdvances = resData.loansAndAdvances || [];
                pIncrementHistory = resData.incrementHistory || [];
            } 
            else if (resData.data && resData.data.staffProfile) {
                profile = resData.data.staffProfile;
                pHistory = resData.data.transferHistory || [];
                pAttendance = resData.data.attendance || [];
                pSalary = resData.data.salary || [];
                pLeaveRequests = resData.data.leaveRequests || [];
                pLoansAndAdvances = resData.data.loansAndAdvances || [];
                pIncrementHistory = resData.data.incrementHistory || [];
            } 
            else if (resData.data && resData.data.data && resData.data.data.staffProfile) {
                profile = resData.data.data.staffProfile;
                pHistory = resData.data.data.transferHistory || [];
                pAttendance = resData.data.data.attendance || [];
                pSalary = resData.data.data.salary || [];
                pLeaveRequests = resData.data.data.leaveRequests || [];
                pLoansAndAdvances = resData.data.data.loansAndAdvances || [];
                pIncrementHistory = resData.data.data.incrementHistory || [];
            } 
            // array fallbacks
            else if (resData.data && Array.isArray(resData.data) && resData.data.length > 0 && resData.data[0].id) {
                profile = resData.data[0];
            } 
            else if (Array.isArray(resData) && resData.length > 0 && resData[0].id) {
                profile = resData[0];
            } 
            // direct object fallback
            else if (resData.id || resData.name) {
                profile = resData;
            }
        }
        
        if (profile && Object.keys(profile).length > 0) {
            this.staffData = profile;
            this.transferHistory = pHistory;
            this.attendance = pAttendance;
            this.salary = pSalary;
            this.leaveRequests = pLeaveRequests;
            this.loansAndAdvances = pLoansAndAdvances;
            this.incrementHistory = pIncrementHistory;
            const envelope = resData.staffProfile
              ? resData
              : resData.data && resData.data.staffProfile
              ? resData.data
              : resData.data && resData.data.data && resData.data.data.staffProfile
              ? resData.data.data
              : resData;
            this.hydrateQualificationsAndCertificates(profile, envelope);
            if (this.isMyskoolIuss) {
              await this.loadIussQualificationDetails();
            }
        } else {
            console.error("Unable to extract staff profile from: ", response);
            this.staffData = null; 
        }

      } catch (error) {
        console.error("Error fetching staff details:", error);
      }
      this.loading = false;
    },
    normalizeQualificationsList(list) {
      if (!Array.isArray(list)) return [];
      return list
        .map((q) => ({
          degree: q.degree || "",
          subject: q.subject || "",
          institute: q.institute || "",
          year: q.year ? String(q.year) : "",
          grade: q.grade || "",
        }))
        .filter((q) => q.degree || q.subject || q.institute || q.year || q.grade);
    },
    normalizeCertificatesList(list) {
      if (!Array.isArray(list)) return [];
      return list
        .map((c) => ({
          name: c.name || "",
          institute: c.institute || "",
          year: c.year ? String(c.year) : "",
        }))
        .filter((c) => c.name || c.institute || c.year);
    },
    hydrateQualificationsAndCertificates(profile, envelope) {
      const sources = [envelope, profile].filter(Boolean);
      let quals = [];
      let certs = [];

      sources.forEach((src) => {
        if (!quals.length && Array.isArray(src.qualifications)) {
          quals = src.qualifications;
        }
        if (!certs.length && Array.isArray(src.certificates)) {
          certs = src.certificates;
        }
        if (!quals.length && src.staff && Array.isArray(src.staff.qualifications)) {
          quals = src.staff.qualifications;
        }
        if (!certs.length && src.staff && Array.isArray(src.staff.certificates)) {
          certs = src.staff.certificates;
        }
      });

      quals = this.normalizeQualificationsList(quals);
      certs = this.normalizeCertificatesList(certs);

      if (!quals.length && profile && profile.qualification) {
        quals = [
          {
            degree: profile.qualification,
            subject: "",
            institute: profile.note || "",
            year: "",
            grade: "",
          },
        ];
      }
      if (!certs.length && profile && profile.certificate) {
        certs = [
          {
            name: profile.certificate,
            institute: "",
            year: "",
          },
        ];
      }

      this.qualifications = quals;
      this.certificates = certs;
    },
    async loadIussQualificationDetails() {
      if (!this.isMyskoolIuss || !this.staffID) return;
      if (this.qualifications.length || this.certificates.length) return;

      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "staff/LoadSelectedApplicantWithInterview?db=" +
            this.$store.state.userData.db +
            "&id=" +
            this.staffID,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        if (!Array.isArray(res) && res && res.data) {
          const payload = res.data;
          this.hydrateQualificationsAndCertificates(
            payload.staff || this.staffData,
            payload
          );
        }
      } catch (error) {
        console.error("Error loading IUSS qualification details:", error);
      }
    },
    async LoadDepartments() {
      try {
        const url =
          this.$store.state.domain +
          "StaffDepartments?cID=" +
          this.$store.state.userData.cId +
          "&db=" +
          this.$store.state.userData.db;

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
          context: this,
        };

        const response = await this.get(obj);
        if (Array.isArray(response)) {
          this.departments = response;
        } else if (response && Array.isArray(response.data)) {
          this.departments = response.data;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async LoadCampuses() {
      try {
        const url =
          this.$store.state.domain +
          "campuses?cID=" +
          this.$store.state.userData.cId +
          "&db=" +
          this.$store.state.userData.db;

        const obj = {
          url: url,
          token: this.$store.state.userData.token,
          context: this,
        };

        const response = await this.get(obj);
        if (Array.isArray(response)) {
          this.campuses = response;
        } else if (response && Array.isArray(response.data)) {
          this.campuses = response.data;
        }
      } catch (error) {
        console.log(error);
      }
    },
    formatDate(dateStr) {
      if (!dateStr || dateStr.startsWith("0001")) return null;
      return new Date(dateStr).toLocaleDateString("en-UK", {
        year: "numeric",
        day: "numeric",
        month: "short",
      });
    },
    formatMoney(val) {
      if (val === null || val === undefined || val === "") return "—";
      const n = Number(val);
      if (Number.isNaN(n)) return String(val);
      return (
        "Rs. " +
        n.toLocaleString("en-PK", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      );
    },
    salaryPeriodLabel(row) {
      if (!row) return "Salary";
      if (row.monthYear && String(row.monthYear).trim()) return String(row.monthYear).trim();
      const months = [
        "", "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ];
      const m = Number(row.month);
      const y = row.year;
      if (m >= 1 && m <= 12 && y) return `${months[m]} ${y}`;
      if (y) return `Year ${y}`;
      return row.date ? row.date.split("T")[0] : "Salary";
    },
    getCampus(cId) {
      if (!cId) return "";
      const ct = this.campuses.find((el) => el.id == cId);
      return ct ? ct.branch : "";
    },
    getDepName(d) {
      if (!d) return "";
      const ct = this.departments.find((el) => el.id == d);
      return ct ? ct.name : "";
    },
  }
};
</script>

<style lang="scss" scoped>
.staff-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  position: relative;
  overflow: hidden;
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

.gradient-top-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  z-index: 10;
}

.bg-light-soft {
  background-color: #fafbfc;
}

.profile-sidebar {
  border-right: 1px solid #f0edff;
}

.profile-banner {
  width: 100%;
  height: 120px;
  background: linear-gradient(135deg, #dfd8ff 0%, #f3f0ff 100%);
  position: relative;
  z-index: 0;
}

.avatar-wrapper {
  position: relative;
  display: inline-block;
  padding: 6px;
  border-radius: 50%;
  background: #ffffff;
  z-index: 2;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
}

.profile-avatar {
  border: 3px solid #f0edff;
}

.status-indicator {
  position: absolute;
  bottom: 12px;
  right: 12px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 3px solid #ffffff;
  z-index: 5;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}

.text-primary-dark {
  color: #2d1b69;
}

.name-title {
  letter-spacing: -0.3px;
  font-size: 1.5rem;
  margin-top: 5px;
}

.designation-text {
  font-size: 0.85rem;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  font-weight: 700;
  color: #7c5dfa !important;
  margin-bottom: 1.2rem;
}

.info-pill {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background: #fdfcff;
  border: 1px solid #f0edff;
  padding: 10px 16px;
  border-radius: 14px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #4b5563;
  transition: all 0.3s ease;
  word-break: break-word;
  line-height: 1.3;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.08);
    border-color: #d4c7ff;
    background: #ffffff;
  }
}

.section-title {
  font-weight: 700;
  color: #2d1b69;
  border-bottom: 2px dashed #f0edff;
  padding-bottom: 10px;
  margin-bottom: 18px;
  font-size: 1.05rem;
  display: flex;
  align-items: center;
}

.info-label {
  font-size: 0.75rem;
  color: #82868b;
  margin-bottom: 4px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.info-value {
  font-size: 0.95rem;
  color: #3f4254;
  font-weight: 600;
  line-height: 1.4;
}

.iuss-details-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.iuss-details-record {
  padding: 0.85rem 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 10px;
  background: #fafafa;
}

.content-grid {
  background: #ffffff;
  padding: 20px 15px;
  border-radius: 16px;
  border: 1px solid #f0edff;
  box-shadow: 0 4px 20px rgba(115, 103, 240, 0.03);
  margin-bottom: 2rem !important;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 6px 25px rgba(115, 103, 240, 0.08);
  }
}

.back-btn {
  border-radius: 10px;
  padding: 8px 14px;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  background: #ffffff;
  color: #7c5dfa !important;
  border: 1px solid #e0d8ff;
  
  &:hover {
    background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
    color: #ffffff !important;
    border-color: transparent;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(124, 93, 250, 0.2) !important;
  }
}

::v-deep .staff-details-tabs-shell {
  margin-top: 0.25rem;
}

::v-deep .staff-details-tabs-shell .staff-details-tab-panes {
  padding-top: 0.5rem;
  min-height: 120px;
}

::v-deep .staff-details-tabs-shell .custom-nav-tabs {
  border-bottom: 2px solid #f0edff;
  margin-bottom: 0.75rem;
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  gap: 0.25rem;
  padding-bottom: 2px;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbb9ff;
    border-radius: 4px;
  }

  .nav-item {
    flex: 0 0 auto;
    margin-bottom: -2px;

    .nav-link {
      border: none !important;
      color: #6b7280;
      font-weight: 600;
      font-size: 0.875rem;
      padding: 0.65rem 1rem;
      margin-right: 0;
      border-radius: 10px 10px 0 0;
      transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease;
      background: transparent;
      white-space: nowrap;
      cursor: pointer;
      user-select: none;
      -webkit-tap-highlight-color: transparent;

      &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.35);
      }

      &:hover {
        color: #7c5dfa;
        background: #f8f6ff;
      }

      &.active,
      &.custom-nav-active {
        color: #5b21b6 !important;
        background: linear-gradient(180deg, #f5f3ff 0%, #ede9fe 100%) !important;
        border-bottom: 3px solid #7c5dfa !important;
        font-weight: 700;
      }
    }
  }
}

.timeline-point--rs {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%) !important;
  border: 1px solid #c4b5fd !important;

  .rs-mark {
    font-size: 0.65rem;
    font-weight: 800;
    color: #5b21b6;
    letter-spacing: 0.02em;
    line-height: 1;
  }
}

.empty-state-rs {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  margin: 0 auto;
  border-radius: 16px;
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
  border: 2px dashed #c4b5fd;
  font-size: 1.25rem;
  font-weight: 800;
  color: #7c5dfa;
}

.modern-table {
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
  border: 1px solid #f0edff;
  
  ::v-deep th {
    background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
    color: #4b5563;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e0d8ff;
    border-top: none;
    padding: 12px;
  }
  
  ::v-deep td {
    vertical-align: middle;
    color: #1f2937;
    font-size: 0.95rem;
    border-top: 1px dashed #f0edff;
    padding: 12px;
  }
}

/* Empty State */
.empty-state-box {
  background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
  border-radius: 16px;
  border: 2px dashed #e5e7eb;
}

.empty-state-icon {
  color: #cbb9ff !important;
}

.empty-state-title {
  font-weight: 700;
  color: #2d1b69;
  letter-spacing: -0.3px;
}

.empty-state-message {
  font-size: 1rem;
  font-weight: 500;
}

.animation-fade {
  animation: fadeIn 0.4s ease-out;
}

.skeleton-loader {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@keyframes slideUpFade {
  0% { opacity: 0; transform: translateY(30px); }
  100% { opacity: 1; transform: translateY(0); }
}

@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

.gap-2 {
  gap: 0.5rem;
}

.scrollable-tab-content {
  max-height: 65vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 15px; /* space for scrollbar to avoid clipping */
}

/* Custom Scrollbar for modern look */
.scrollable-tab-content::-webkit-scrollbar {
  width: 6px;
}
.scrollable-tab-content::-webkit-scrollbar-track {
  background: transparent;
}
.scrollable-tab-content::-webkit-scrollbar-thumb {
  background: #dbdbdb;
  border-radius: 6px;
}
.scrollable-tab-content::-webkit-scrollbar-thumb:hover {
  background: #b3b3b3;
}

/* Timeline Custom Styles */
.vuexy-timeline {
  position: relative;
  padding-left: 10px;
}
.vuexy-timeline .timeline-item {
  position: relative;
  padding-bottom: 2.5rem;
  padding-left: 2.5rem;
  border-left: 2px solid #ebe9f1;
}
.vuexy-timeline .timeline-item:last-child {
  border-left-color: transparent;
  padding-bottom: 0;
}
.vuexy-timeline .timeline-point {
  position: absolute;
  left: -20px;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background-color: #fff;
  border: 2px solid #7367f0;
  border-radius: 50%;
  color: #7367f0;
  box-shadow: 0 4px 8px rgba(115, 103, 240, 0.15);
  z-index: 2;
}
.vuexy-timeline .timeline-event {
  background-color: #ffffff;
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid #ebe9f1;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
  transition: all 0.3s ease-in-out;
}
.vuexy-timeline .timeline-event:hover {
  box-shadow: 0 6px 20px rgba(115, 103, 240, 0.08);
  border-color: rgba(115, 103, 240, 0.3);
  transform: translateY(-2px);
}
.timeline-badge {
  padding: 0.4rem 0.6rem;
  font-size: 85%;
}
.letter-spacing {
  letter-spacing: 0.5px;
}
</style>
